import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { TabPanel } from 'primereact/tabview'
import { MutationActionCreatorResult } from '@reduxjs/toolkit/query'

import { ChargePoint, useOcppCommandDataTransferMutation } from '../../../common/redux/api/web-api'
import { ChargePointDawnControlWidget } from '../../../lib/charge-point-dawn-control-widget'
import { ChargePointOcppAuraControlWidget } from '../../../lib/charge-point-ocpp-aura-control-widget'
import { ChargePointOcppHaloControlWidget } from '../../../lib/charge-point-ocpp-halo-control-widget'
import isAura from '../../../lib/isAura'
import isDawn from '../../../lib/isDawn'
import isHalo from '../../../lib/isHalo'
import { ChargePointOcppCommandsWidget } from '../../components/charge-point-ocpp-commands-widget'
import { ChargePointOcppConfigWidget } from '../../components/charge-point-ocpp-config-widget'
import { ChargePointOcppLogsWidget } from '../../../common/components/charge-point-ocpp-logs-widget'
import { ChargepointLatestChargingSessionsWidget } from '../../../common/components/chargepoint-latest-charging-sessions-widget'
import {
    isAdminDomain,
    isLandingPage,
    isPartnerDomain,
    isSupportDomain,
    isUserappDomain,
} from '../../../lib/currentDomain'
import useRemoteControlAutoRefresh from '../../../common/hooks/useRemoteControlAutoRefresh'
import { ChargePointPropertiesWidget } from '../../features/charge-point-properties-widget'
import { ChargePointOcppRelayLogsWidget } from '../../../common/components/charge-point-ocpp-relay-logs-widget'
import { OcppElectricalSettings } from '../../components/ocpp-electrical-settings'
import { ChargepointDetailsWidget } from '../../../common/features/chargepoint-details-widget'
import { ChargePointAlarmsWidget } from '../../../partner/components/charge-point-alarms-widget'
import { ChargePointMaintenanceLogsWidget } from '../../../partner/components/charge-point-maintenance-logs-widget'
import { ChargePointPartnerInfoWidget } from '../../../common/features/charge-point-partner-info-widget/charge-point-partner-info-widget'
import isLuna from '../../../lib/isLuna'
import { ChargePointLunaControlWidget } from '../../../lib/charge-point-luna-control-widget'
import useText from '../../../common/hooks/useText'
import { ChargePointOverviewWidget } from '../../../common/components/charge-point-overview-widget'
import { ChargePointSchedulesPage } from '../../../ui/ChargePointSchedulesPage'
import isOnline from '../../../lib/isOnline'
import { LazyTabView } from '../../../common/uikit/LazyTabView'

export const SContainer = styled.div`
    margin: 0 -15px -15px;
    padding: 0 15px 30px;
`

export const SWidgetGroupsContainer = styled.div`
    display: flex;
    gap: 30px;
    justify-content: space-between;

    @media (max-width: 990px) {
        flex-direction: column;
        gap: 20px;
    }
`

export const SWidgetsGroup = styled.div<{ width?: string }>`
    display: flex;
    flex-direction: column;
    width: ${({ width }) => width};
    width: 50%;

    @media (max-width: 990px) {
        width: 100%;
    }
`
const LogsContainer = styled.div<{ splitLogs?: boolean }>`
    display: flex;
    flex-direction: column;
    ${({ splitLogs }) =>
        splitLogs
            ? `
        gap: 30px;
        flex-direction: row;
    `
            : ''}
    @media (max-width: 990px) {
        flex-direction: column;
    }
`

export interface ChargePointOcppPageProps {
    chargePoint: ChargePoint
    isLoading: boolean
}

export function ChargePointOcppPage({ chargePoint, isLoading }: ChargePointOcppPageProps): React.ReactElement | null {
    const remoteControlData =
        !(isUserappDomain && isLandingPage(window.location.pathname)) && useRemoteControlAutoRefresh(chargePoint.id)

    const sendRequest = async (request: MutationActionCreatorResult<T>) => {
        await request.unwrap()
    }
    const text = useText()

    const [dataTransferRequest] = useOcppCommandDataTransferMutation()
    const dataTransfer = useCallback(
        (messageId: string) => {
            if (isPartnerDomain || isSupportDomain) {
                sendRequest(
                    dataTransferRequest({
                        chargePointId: chargePoint.id,
                        dataTransferReq: {
                            vendorId: 'com.chargeamps',
                            messageId,
                        },
                    })
                )
            }
        },
        [chargePoint.id, dataTransferRequest]
    )

    const chargerIsOnline = isOnline(chargePoint)

    useEffect(() => {
        if (chargerIsOnline) {
            dataTransfer(`networkStatus`)
        }
    }, [chargerIsOnline, dataTransfer])
    const [splitLogs, setSplitLogs] = useState(false)
    const updatedChargePointStatus = remoteControlData?.chargePointStatus

    return (
        <LazyTabView>
            <TabPanel header={text('lblPropertiesWidget')} leftIcon="pi pi-list mr-2">
                <div className="grid">
                    <div className="lg:col-6 col-12">
                        <ChargePointPropertiesWidget chargePoint={chargePoint} disabled={isLoading} />
                    </div>
                    <div className="lg:col-6 col-12">
                        {remoteControlData && (
                            <>
                                <ChargePointOverviewWidget chargePoint={remoteControlData} />

                                <div className="mt-4">
                                    {isHalo(chargePoint) && (
                                        <ChargePointOcppHaloControlWidget
                                            chargePoint={remoteControlData}
                                            isReadOnly={false}
                                        />
                                    )}
                                    {isAura(chargePoint) && (
                                        <ChargePointOcppAuraControlWidget
                                            chargePoint={remoteControlData}
                                            isReadOnly={false}
                                        />
                                    )}
                                    {isDawn(chargePoint) && (
                                        <ChargePointDawnControlWidget
                                            chargePoint={remoteControlData}
                                            isReadOnly={false}
                                        />
                                    )}
                                    {isLuna(chargePoint) && (
                                        <ChargePointLunaControlWidget chargePoint={remoteControlData} />
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </TabPanel>
            <TabPanel
                header={
                    isSupportDomain || (isPartnerDomain && chargePoint?.ocppEnabled)
                        ? text('btnIntegrations')
                        : text('lblChargerSettings')
                }
                leftIcon="pi pi-cog mr-2"
            >
                <ChargepointDetailsWidget chargePoint={chargePoint} />
            </TabPanel>
            <TabPanel header={text('lblElectricalSettings')} leftIcon="pi pi-bolt mr-2">
                <div className="grid">
                    <div className=" col-12">
                        <OcppElectricalSettings chargePoint={remoteControlData ?? chargePoint} />
                    </div>
                </div>
            </TabPanel>
            {!isAdminDomain && (
                <TabPanel header={text('lblOcppLogsWidget')} leftIcon="pi pi-receipt mr-2">
                    {isSupportDomain || isPartnerDomain ? (
                        <LogsContainer splitLogs={splitLogs}>
                            <ChargePointOcppLogsWidget
                                chargePointId={chargePoint.id}
                                splitViewAvailable={chargePoint?.isOcpp && chargePoint?.ocppEnabled}
                                splitView={splitLogs}
                                onToggleSplitView={() => setSplitLogs((value) => !value)}
                            />

                            {chargePoint.ocppEnabled ? (
                                <ChargePointOcppRelayLogsWidget chargePointId={chargePoint.id} />
                            ) : null}
                        </LogsContainer>
                    ) : null}
                </TabPanel>
            )}

            {((isAdminDomain && !chargePoint?.ocppEnabled) || isSupportDomain) && (
                <TabPanel header={text('lblSchedules')} leftIcon="pi pi-calendar mr-2">
                    <ChargePointSchedulesPage updatedChargePointStatus={updatedChargePointStatus} />
                </TabPanel>
            )}

            {(!isAdminDomain || (isPartnerDomain && !chargePoint?.ocppEnabled)) && (
                <TabPanel header={text('lblChargingSessionsWidget')} leftIcon="pi pi-wave-pulse mr-2">
                    {
                        <ChargepointLatestChargingSessionsWidget
                            chargePointId={chargePoint.id}
                            useFilter={true}
                            deleteEnabled={!isPartnerDomain}
                        />
                    }
                </TabPanel>
            )}

            {isSupportDomain && (
                <TabPanel header={text('lblOcppCommandsWidget')} leftIcon="pi pi-file-import mr-2">
                    <div className="grid">
                        <div className="lg:col-6 col-12">
                            <ChargePointOcppCommandsWidget chargePoint={chargePoint} />
                        </div>
                        <div className="lg:col-6 col-12">
                            <ChargePointOcppConfigWidget chargePoint={remoteControlData ?? chargePoint} />
                        </div>
                    </div>
                </TabPanel>
            )}
            {((isPartnerDomain && !chargePoint?.ocppEnabled) || isSupportDomain) && (
                <TabPanel header={text('lblAlerts')} leftIcon="pi pi-bell mr-2 ">
                    <ChargePointAlarmsWidget />

                    <div className="mt-3">
                        <ChargePointMaintenanceLogsWidget />
                    </div>
                </TabPanel>
            )}
            {isAdminDomain && (
                <TabPanel header={text('lbllinkSupport')} leftIcon="pi pi-phone mr-2">
                    <ChargePointPartnerInfoWidget chargePointId={chargePoint.id} />
                </TabPanel>
            )}
        </LazyTabView>
    )
}
